import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconPencilOutline18 as Pencil,
	IconPlusOutline18 as Plus,
	IconToggleOutline18 as ToggleLeft,
	IconToggle2Outline18 as ToggleRight,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"

import {
	AlertDialog,
	Button,
	Dot,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
} from "@productlane/ui"
import { EllipsisIcon as MoreHorizontal } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { queries } from "@productlane/zero/queries"

import type { WebhookListRow } from "./shared"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import { SettingsSection } from "../../../settings-switches/settings-section"
import { CreateWebhookDialog } from "./create-webhook-dialog"
import { webhookEditPath, webhookViewPath } from "./shared"

export function WebhooksBlock() {
	const [hooks] = useQuery(queries.webhooks())
	const [isCreateOpen, setIsCreateOpen] = useState(false)

	return (
		<>
			<SettingsSection
				title="Webhooks"
				description="Webhooks allow you to receive HTTP requests when an entity is created, updated, or deleted."
			/>
			<SettingsContainer className="mb-0 mt-4 px-0 pb-0">
				{hooks.length === 0 ? (
					<div className="flex items-center justify-between px-4 pb-4">
						<div className="text-sm text-secondary">No webhooks</div>
						<Button
							variant="ghost"
							Icon={Plus}
							onClick={() => setIsCreateOpen(true)}
							className="text-primary"
						>
							New webhook
						</Button>
					</div>
				) : (
					<div className="flex flex-col">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 pb-4 dark:border-gray-700/80">
							<div className="text-sm font-medium">
								{hooks.length} {hooks.length === 1 ? "webhook" : "webhooks"}
							</div>
							<Button
								variant="ghost"
								className="text-primary"
								Icon={Plus}
								onClick={() => setIsCreateOpen(true)}
							>
								New webhook
							</Button>
						</div>
						<div className="flex flex-col">
							{hooks.map((hook) => (
								<WebhookRow key={hook.id} hook={hook} />
							))}
						</div>
					</div>
				)}
			</SettingsContainer>
			<CreateWebhookDialog
				isOpen={isCreateOpen}
				onClose={() => setIsCreateOpen(false)}
			/>
		</>
	)
}

function WebhookRow({ hook }: { hook: WebhookListRow }) {
	const navigate = useNavigate()
	const ctx = api.useUtils()
	const [confirmDelete, setConfirmDelete] = useState(false)

	const goToView = () => void navigate({ href: webhookViewPath(hook.id) })
	const goToEdit = () => void navigate({ href: webhookEditPath(hook.id) })

	const updateMutation = api.workspace.updateWebhook.useMutation({
		onSuccess: () => ctx.workspace.invalidate(),
	})
	const deleteMutation = api.workspace.deleteWebhook.useMutation({
		onSuccess: () => {
			toast.success({ title: "Webhook deleted" })
			void ctx.workspace.invalidate()
		},
	})

	return (
		<>
			<div
				className="group flex cursor-default items-center gap-3 border-b border-gray-200 px-4 py-4 last:rounded-b-lg last:border-b-0 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:border-gray-700/80 dark:hover:bg-gray-900 dark:focus-visible:bg-gray-900"
				onClick={goToView}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault()
						goToView()
					}
				}}
			>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<div className="truncate text-sm font-medium">{hook.label}</div>
					</div>
					<div className="flex flex-row items-center gap-2 truncate text-xs text-secondary">
						{hook.active ? (
							<Dot size="sm" color="green" />
						) : (
							<Dot size="sm" color="gray" />
						)}
						{hook.url}
					</div>
				</div>
				<div
					role="presentation"
					onClick={(e) => e.stopPropagation()}
					onKeyDown={(e) => e.stopPropagation()}
				>
					<DropdownMenu
						contentProps={{ align: "end" }}
						trigger={<IconButton Icon={MoreHorizontal} variant="ghost" />}
					>
						<DropdownMenuItem onClick={goToEdit} Icon={Pencil}>
							Edit
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() =>
								updateMutation.mutate({ id: hook.id, active: !hook.active })
							}
							Icon={hook.active ? ToggleLeft : ToggleRight}
						>
							{hook.active ? "Disable" : "Enable"}
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => setConfirmDelete(true)}
							Icon={Trash2}
						>
							Delete
						</DropdownMenuItem>
					</DropdownMenu>
				</div>
			</div>
			<AlertDialog
				isOpen={confirmDelete}
				onOpenChange={setConfirmDelete}
				title="Delete webhook?"
				description={`The webhook "${hook.label}" will stop receiving events. This cannot be undone.`}
				confirmText="Delete"
				onCancel={() => setConfirmDelete(false)}
				onConfirm={() => {
					deleteMutation.mutate({ id: hook.id })
					setConfirmDelete(false)
				}}
			/>
		</>
	)
}
